import moment from 'moment';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Text, View } from 'react-native';
import { TextInput as CropCircleTextInput } from '../../crop-circle/Components/TextInput/TextInput';
import { Toggle } from '../../crop-circle/Components/Toggle/Toggle';
import { Icon } from '../../crop-circle/icons';
import { tokens } from '../../crop-circle/tokens';
import { DateRangeSelector, useFormContext } from '../../forms';
import { AlertInputs } from './types';
import { Colors } from '../../../themes/colors';
import { themedStyleSheet } from '../../../themes/themedStyleSheet';

type AlertOptionsProps = {
  /** Only the alert shapes Auto Reset can act on offer the toggle. */
  showAutoReset?: boolean;
};

export const AlertOptions = ({ showAutoReset = false }: AlertOptionsProps) => {
  const { t } = useTranslation();
  const { setValue, control, getValues } = useFormContext();
  const autoReset = useWatch({ control, name: AlertInputs.autoReset });

  const alertName = getValues(AlertInputs.alertLabel);
  const alertNamePlaceholder = getValues(AlertInputs.alertLabelPlaceholder);

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t('alerts.customLabelTitle')}</Text>
      <CropCircleTextInput
        value={alertName}
        onChangeText={(text) =>
          setValue(AlertInputs.alertLabel, text, { shouldDirty: false })
        }
        placeholder={alertNamePlaceholder || t('common.optional')}
        textArea={true}
        numberOfLines={2}
        autoExpand={true}
        backgroundColor={Colors.role.surfaceHover}
        hideBorder={true}
        style={styles.textInputWrapper}
      />

      <Text style={styles.title}>{t('alerts.blackoutDates')}</Text>
      <DateRangeSelector
        control={control}
        setValue={setValue}
        maxYears={5}
        minDate={moment().format('YYYY-MM-DD')}
        styleContainer={styles.dateRangeContainer}
      />

      {showAutoReset && (
        <View style={styles.autoResetRow}>
          <Icon name="Reset" size={20} color={Colors.role.textStrong} />
          <View style={styles.autoResetContent}>
            <Text style={styles.autoResetTitle}>{t('alerts.autoReset')}</Text>
            <Text style={styles.autoResetSubtitle}>
              {t('alerts.autoResetDescription')}
            </Text>
          </View>
          <Toggle
            value={!!autoReset}
            onValueChange={(value) =>
              setValue(AlertInputs.autoReset, value, { shouldDirty: true })
            }
            colorScheme="success"
          />
        </View>
      )}
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    minHeight: 0,
  },
  title: {
    ...tokens.typography.body.small,
    fontWeight: '600',
    color: role.textStrong,
    marginBottom: tokens.spacing.sm,
  },
  textInputWrapper: {
    marginBottom: tokens.spacing.lg,
  },
  dateRangeContainer: {
    flexDirection: 'row',
    gap: tokens.spacing.sm,
  },
  // Mirrors the Pin to Asset row in issueEdit.
  autoResetRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
    marginTop: tokens.spacing.lg,
  },
  autoResetContent: {
    flex: 1,
  },
  autoResetTitle: {
    ...tokens.typography.body.smallEmphasised,
    color: role.textStrong,
  },
  autoResetSubtitle: {
    ...tokens.typography.label.medium,
    color: role.textSecondary,
    marginTop: 2,
  },
}));
